
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Plus, Users } from "lucide-react";
import { useAuth } from "@/app/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { EventType } from "@/types/event";
import EventCard from "@/components/events/EventCard";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import CustomCalendarDialog from "@/components/calendar/CustomCalendarDialog";

const Dashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [upcomingEvents, setUpcomingEvents] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchEvents = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Get current date in ISO format
        const now = new Date().toISOString();
        
        // Fetch upcoming events for the user from Supabase
        const { data, error } = await supabase
          .from('ft_events')
          .select(`
            *,
            ft_groups(id, name, color)
          `)
          .eq('created_by', user.id)
          .gte('start_date', now)
          .order('start_date', { ascending: true })
          .limit(5);
          
        if (error) {
          console.error('Error fetching events:', error);
          return;
        }
        
        // Transform data to match EventType
        const formattedEvents = data.map(event => {
          // Get group data safely
          let groupName = "";
          let groupColor = "bg-blue-100 text-blue-800"; // Default color
          
          if (event.ft_groups) {
            // Type assertion to access properties safely
            const groups = event.ft_groups as { name: string; color: string };
            groupName = groups?.name || "";
            groupColor = groups?.color || "bg-blue-100 text-blue-800";
          }
          
          // Build a proper EventType object
          return {
            id: event.id,
            title: event.title,
            description: event.description || "",
            startDate: event.start_date,
            endDate: event.end_date,
            location: event.location || "",
            created_by: event.created_by,
            group: groupName,
            color: groupColor,
            attendees: [], // This will be populated when expanded
          };
        });
        
        setUpcomingEvents(formattedEvents);
      } catch (err) {
        console.error('Error in fetchEvents:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [user]);
  
  const handleCreateEvent = () => {
    navigate("/create-event");
  };
  
  const handleViewEvent = (event: EventType) => {
    // Open event detail view
    navigate(`/events?event=${event.id}`);
  };
  
  const getEventCountBadge = () => {
    if (loading) return null;
    return (
      <Badge variant="outline" className="ml-2">
        <span className="text-black">{upcomingEvents.length}</span>
      </Badge>
    );
  };
  
  return (
    <>
      <h1 className="text-3xl font-bold text-black">Dashboard</h1>
      <p className="text-muted-foreground mb-6">Welcome{user ? ', ' + (user.firstName || user.email) : ''}!</p>
      
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex flex-col space-y-1.5">
              <CardTitle className="text-black">
                Upcoming Events {getEventCountBadge()}
              </CardTitle>
            </div>
            <Button size="sm" onClick={handleCreateEvent}>
              <Plus className="mr-2 h-4 w-4" />
              <span className="text-white">Add Event</span>
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="space-y-4">
                {upcomingEvents.map((event) => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onClick={() => handleViewEvent(event)} 
                  />
                ))}
                {upcomingEvents.length > 0 && (
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => navigate('/calendar')}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    <span className="text-black">View All Events</span>
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2 text-black">No upcoming events</h3>
                <p className="text-muted-foreground mb-4">
                  You don't have any upcoming events scheduled.
                </p>
                <Button onClick={handleCreateEvent}>
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="text-white">Create Event</span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex flex-col space-y-1.5">
              <CardTitle className="text-black">Friends & Family</CardTitle>
            </div>
            <Button size="sm" onClick={() => navigate('/friends-and-family')}>
              <Users className="mr-2 h-4 w-4" />
              <span className="text-white">Manage</span>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2 text-black">Stay Connected</h3>
              <p className="text-muted-foreground mb-4">
                Connect with your friends and family to share events and activities.
              </p>
              <Button onClick={() => navigate('/friends-and-family')}>
                <Users className="mr-2 h-4 w-4" />
                <span className="text-white">Friends & Family</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {user && user.plan === 'pro' && (
          <Card className="md:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex flex-col space-y-1.5">
                <CardTitle className="text-black">
                  Custom Calendars
                  <Badge variant="outline" className="bg-green-100 text-green-800 ml-2">
                    PRO
                  </Badge>
                </CardTitle>
              </div>
              <CustomCalendarDialog />
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2 text-black">Organize Your Schedule</h3>
                <p className="text-muted-foreground mb-4">
                  Create up to 3 custom calendars to organize different aspects of your life.
                </p>
                <CustomCalendarDialog />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default Dashboard;
